import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  ACCESS_TOKEN_STORAGE_KEY,
  PATH,
  PROFILE_STORAGE_FIELDS,
} from '../../constants'
import { useAuthTokenApi } from '../useAuthTokenApi'
import { useCreateNextPath } from '../useCreateNextPath'

const REDIRECT_URL = process.env.VITE_OAUTH_REDIRECT_URL || ''

const reservationUrlPattern = /^\/hotels\/reservations|^\/spas\/reservations/

const sessionStorageQueryParamKeys = [
  'checkInDate',
  'checkOutDate',
  'numberOfAdults',
  'numberOfChildren',
  'numberOfInfants',
  'page',
  'roomCount',
  'showAll',
  'roomOrder',
  'planOrder',
  'order',
  'room', // Secret room
  'discountCode', // Coupon code carried from campaign URLs through login
  'invoiceKey', // Invoice billing key; guests often open the invoice URL signed out
  'fromPlanRooms', // Plan-first origin marker, restored so Back works post-login
]

const clearSessionStorage = () => {
  try {
    sessionStorageQueryParamKeys.forEach((key) => {
      sessionStorage.removeItem(`query.${key}`)
    })
    sessionStorage.removeItem(`query.next`)
  } catch (e) {
    // Do nothing
  }
}

const restoreQueryParams = () => {
  const params: { [key: string]: string } = {}
  sessionStorageQueryParamKeys.forEach((key) => {
    try {
      const value = sessionStorage.getItem(`query.${key}`) || ''
      if (key === 'page' && value === '1') {
        return
      }
      params[key] = value
    } catch (e) {
      // Do nothing
    }
  })
  return params
}

export const useLoginCallback = (code: string | null) => {
  const navigate = useNavigate()
  const { onCreateNextPath } = useCreateNextPath()

  // Sending raw url blocks AWS WAF(response 403),
  // so we encode Base64 encoded value.
  const { trigger } = useAuthTokenApi(code, btoa(REDIRECT_URL))
  // For avoid React.StrictMode render twice at development mode
  const ref = useRef(true)
  useEffect(() => {
    if (code) {
      if (process.env.NODE_ENV === 'development') {
        if (ref.current) {
          ref.current = false
          return
        }
      }
      // trigger() returns a Promise. try/catch around the call without
      // awaiting only catches a synchronous throw, so a 401 from the
      // token endpoint used to become an unhandledrejection (Sentry
      // FVILLAGE-FRONTEND-9) and leave the guest on /login/callback.
      trigger()
        .then((data) => {
          sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, data?.token ?? '')
          PROFILE_STORAGE_FIELDS.forEach((field) =>
            sessionStorage.setItem(field, data?.[field] ?? '')
          )

          const next = sessionStorage.getItem('query.next') ?? '/'
          if (next === '/') {
            clearSessionStorage()
            navigate(next)
            return
          }
          const queryParams = restoreQueryParams()
          clearSessionStorage()

          // If path is reservations, ignore query params.
          const params = reservationUrlPattern.exec(next)
            ? { clearQueryParams: true }
            : { queryParams }
          const nextPath = onCreateNextPath(next, params)

          navigate(nextPath)
        })
        .catch(() => {
          // Login failed, always redirect to login error
          navigate(PATH.errors.loginError)
        })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}
